import { create } from "zustand";
import type { UserHoursSummary, UserDashboard, Transaction } from "@/types";
import { usersApi, transactionsApi } from "@/lib/api";
import { supabase, isSupabaseConfigured } from "@/lib/supabase";

/**
 * User Store
 * Správa user-specific dát (hodiny, transakcie, dashboard data) pomocou Supabase
 */

export interface UpcomingLesson {
  id: number;
  title: string;
  start_at: string;
  type: "group" | "private";
  location?: string;
}

interface UserStore {
  // State
  hours: UserHoursSummary | null;
  dashboard: UserDashboard | null;
  transactions: Transaction[];
  upcomingLessons: UpcomingLesson[];
  isLoading: boolean;
  isLoadingUpcoming: boolean;
  error: string | null;

  // Actions
  fetchHours: (userId: string) => Promise<void>;
  fetchDashboard: (userId: string) => Promise<void>;
  fetchTransactions: (userId: string) => Promise<void>;
  fetchUpcomingLessons: (userId: string) => Promise<void>;
  updateHours: (hours: UserHoursSummary) => void;
  clearUserData: () => void;

  // Realtime callbacks
  handleTransactionChange: () => void;
}

export const useUserStore = create<UserStore>((set, get) => ({
  // Initial state
  hours: null,
  dashboard: null,
  transactions: [],
  upcomingLessons: [],
  isLoading: false,
  isLoadingUpcoming: false,
  error: null,

  /**
   * Načítať zostatky hodín používateľa
   */
  fetchHours: async (userId: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await usersApi.hours(userId);

      if (response.success && response.data) {
        set({ hours: response.data });
      } else {
        set({ error: response.error || "Failed to fetch hours" });
      }
    } catch (error) {
      set({ error: error instanceof Error ? error.message : "Unknown error" });
    } finally {
      set({ isLoading: false });
    }
  },

  /**
   * Načítať dashboard data používateľa
   */
  fetchDashboard: async (userId: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await usersApi.dashboard(userId);

      if (response.success && response.data) {
        // Aktualizovať aj hours z dashboard response (pre konzistenciu)
        set({
          dashboard: response.data,
          hours: response.data.hours,
        });
      } else {
        set({ error: response.error || "Failed to fetch dashboard" });
      }
    } catch (error) {
      set({ error: error instanceof Error ? error.message : "Unknown error" });
    } finally {
      set({ isLoading: false });
    }
  },

  /**
   * Načítať budúce lekcie používateľa
   */
  fetchUpcomingLessons: async (userId: string) => {
    set({ isLoadingUpcoming: true, error: null });
    try {
      const response = await usersApi.upcomingLessons(userId);
      if (response.success && response.data) {
        set({ upcomingLessons: response.data });
      } else {
        set({ upcomingLessons: [] });
        if (response.error) set({ error: response.error });
      }
    } catch (error) {
      set({ upcomingLessons: [] });
      set({
        error:
          error instanceof Error ? error.message : "Failed to fetch lessons",
      });
    } finally {
      set({ isLoadingUpcoming: false });
    }
  },

  /**
   * Načítať transakcie používateľa (cez transactionsApi)
   */
  fetchTransactions: async (userId: string) => {
    set({ isLoading: true, error: null });
    try {
      if (!isSupabaseConfigured) {
        set({ transactions: [] });
        return;
      }

      const response = await transactionsApi.list({
        user_id: userId,
        limit: 50,
      });

      if (response.success && response.data) {
        set({ transactions: response.data });
      } else {
        set({ transactions: [] });
        if (response.error) {
          set({ error: response.error });
        }
      }
    } catch (error) {
      console.error("Transactions fetch error:", error);
      set({
        transactions: [],
        error:
          error instanceof Error
            ? error.message
            : "Failed to fetch transactions",
      });
    } finally {
      set({ isLoading: false });
    }
  },

  /**
   * Aktualizovať hodiny (po check-in, purchase, atď.)
   */
  updateHours: (hours: UserHoursSummary) => {
    set({ hours });

    // Ak máme dashboard, aktualizujeme aj tam
    const { dashboard } = get();
    if (dashboard) {
      set({
        dashboard: {
          ...dashboard,
          hours,
        },
      });
    }
  },

  /**
   * Vyčistiť user data (pri logout)
   */
  clearUserData: () => {
    set({
      hours: null,
      dashboard: null,
      transactions: [],
      upcomingLessons: [],
      error: null,
    });
  },

  /**
   * Realtime: Handler pre zmeny v transactions
   */
  handleTransactionChange: async () => {
    // Refresh transactions, hodiny a dashboard (pre aktualizáciu balíkov)
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (user) {
      await get().fetchTransactions(user.id);
      await get().fetchHours(user.id);
      // Aktualizovať aj dashboard, aby sa hodiny zobrazili správne
      await get().fetchDashboard(user.id);
    }
  },
}));
